import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, ChevronDown, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface AIAssistantWidgetProps {
  pageContext?: 'homepage' | 'calculator' | 'quote' | 'blog' | 'contact' | 'learn';
  userInfo?: {
    name: string;
    age: string;
    state: string;
    coverageAmount: string;
  };
  className?: string;
}

const getContextualGreeting = (context: string = 'homepage', userInfo?: any) => {
  const greetings = {
    calculator: "Want help estimating coverage? I can run the DIME method with you.",
    quote: "I'll help you complete these 4 quick questions.",
    blog: "Have questions about this topic? Ask me anything and I'll guide you to coverage.",
    learn: "Have questions about this topic? Ask me anything and I'll guide you to coverage.",
    contact: "Hi! I'm here to answer any questions about life insurance coverage.",
    homepage: "I'll compare top carriers and find your best match in minutes."
  };

  const baseGreeting = greetings[context as keyof typeof greetings] || greetings.homepage;
  
  if (userInfo) {
    return `Hi ${userInfo.name}! I see you're looking for ${userInfo.coverageAmount} in life insurance coverage in ${userInfo.state}. ${baseGreeting}`;
  }
  
  return `Hi! I'm your AI insurance assistant. ${baseGreeting}`;
};

export const AIAssistantWidget: React.FC<AIAssistantWidgetProps> = ({ 
  pageContext = 'homepage',
  userInfo,
  className 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{role: string, content: string}>>([]);
  const [showNewMessagesPill, setShowNewMessagesPill] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Initialize messages with contextual greeting
  useEffect(() => {
    const initialMessage: Message = {
      id: '1',
      content: getContextualGreeting(pageContext, userInfo),
      isUser: false,
      timestamp: new Date()
    };
    setMessages([initialMessage]);
  }, [pageContext, userInfo]);

  // Load state from localStorage
  useEffect(() => {
    const savedOpen = localStorage.getItem('aiAssistantOpen') === 'true';
    const savedMinimized = localStorage.getItem('aiAssistantMinimized') === 'true';
    setIsOpen(savedOpen);
    setIsMinimized(savedMinimized);
  }, []);

  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem('aiAssistantOpen', isOpen.toString());
    localStorage.setItem('aiAssistantMinimized', isMinimized.toString());
  }, [isOpen, isMinimized]);

  // Smart auto-scroll to bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const isNearBottom = container.scrollTop >= container.scrollHeight - container.clientHeight - 24;
    
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowNewMessagesPill(false);
    } else {
      setShowNewMessagesPill(true);
    }
  }, [messages]);

  // Handle scroll to detect if user scrolled away from bottom
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const isNearBottom = container.scrollTop >= container.scrollHeight - container.clientHeight - 24;
    setShowNewMessagesPill(!isNearBottom && messages.length > 1);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMessagesPill(false);
  };

  const sendToChatbot = async (message: string): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('insurance-chatbot', {
        body: { 
          message, 
          conversationHistory 
        }
      });

      if (error) throw error;

      const aiResponse = data.response;
      setConversationHistory(data.conversationHistory);

      // Check if AI has collected all user data
      try {
        const parsedResponse = JSON.parse(aiResponse);
        if (parsedResponse.dataComplete && parsedResponse.userData) {
          // Get insurance recommendation
          const recommendationResponse = await supabase.functions.invoke('get-insurance-recommendation', {
            body: parsedResponse.userData
          });

          if (recommendationResponse.error) throw recommendationResponse.error;

          const recommendation = recommendationResponse.data;
          
          // Add recommendation message to chat
          const recommendationMessage = `Based on your answers, we recommend you check out ${recommendation.recommendedCompany}. ${recommendation.description}. Click here to get your quote: ${recommendation.referralLink}`;
          
          setTimeout(() => {
            const botMessage: Message = {
              id: Date.now() + 1 + '',
              content: recommendationMessage,
              isUser: false,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, botMessage]);
          }, 1000);
          
          return "Thank you for providing all the information! Let me get you the best recommendation...";
        }
      } catch (parseError) {
        // Not JSON, continue normal conversation
      }

      return aiResponse;
    } catch (error) {
      console.error('Error calling chatbot:', error);
      return "I'm sorry, I'm having trouble connecting right now. Please try again.";
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const aiResponse = await sendToChatbot(inputValue);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleToggleOpen = () => {
    if (isMinimized) {
      setIsMinimized(false);
    }
    setIsOpen(!isOpen);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsOpen(false);
  };

  const handleExpand = () => {
    setIsMinimized(false);
    setIsOpen(true);
  };

  // Minimized edge tab
  if (isMinimized) {
    return (
      <div className={cn("fixed bottom-4 right-0 z-40", className)}>
        <Button
          onClick={handleExpand}
          className="h-12 w-12 rounded-l-xl rounded-r-none bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg border-r-0"
          aria-label="Expand AI Insurance Assistant"
          aria-expanded="false"
        >
          <MessageCircle className="h-5 w-5" />
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40", className)}>
      {/* FAB */}
      {!isOpen && (
        <Button
          onClick={handleToggleOpen}
          className="h-14 w-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl transition-all duration-200 hover:scale-105"
          aria-label="AI Insurance Assistant"
          aria-expanded="false"
          title="AI Insurance Assistant"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Panel */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fixed inset-0 bg-black/20 md:hidden z-30"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div 
            className={cn(
              "fixed transition-transform duration-300 ease-out z-40",
              // Mobile: slide from bottom
              "bottom-0 left-0 right-0 md:bottom-auto md:left-auto md:right-6",
              // Desktop: slide from right
              "md:bottom-6 md:top-auto md:w-96 md:h-auto md:max-h-[680px]",
              // Animation classes
              isOpen ? "translate-y-0 md:translate-x-0" : "translate-y-full md:translate-x-full"
            )}
            style={{
              height: 'min(85vh, 90dvh)',
              maxHeight: '680px',
            }}
          >
            <Card className="border-0 shadow-2xl w-full h-full flex flex-col overflow-hidden">
              {/* Fixed Header */}
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground rounded-t-xl sticky top-0 z-10 backdrop-blur-sm border-b border-primary-foreground/10">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold">AI Insurance Assistant</CardTitle>
                  <CardDescription className="text-primary-foreground/80 text-sm">
                    Get personalized quotes and guidance
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMinimize}
                    className="h-8 w-8 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                    aria-label="Minimize"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    className="h-8 w-8 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
                {/* Scrollable Messages Container */}
                <div 
                  ref={messagesContainerRef}
                  className="flex-1 overflow-y-auto overscroll-contain p-3 md:p-4 space-y-3 relative"
                  onScroll={handleScroll}
                  role="log"
                  aria-live="polite"
                  aria-label="Chat messages"
                >
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex",
                        message.isUser ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] rounded-xl px-4 py-2 text-sm",
                          message.isUser
                            ? "bg-primary text-primary-foreground"
                            : "bg-accent text-accent-foreground border"
                        )}
                      >
                        {message.content}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-accent text-accent-foreground border rounded-xl px-4 py-2 text-sm">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                  
                  {/* New Messages Pill */}
                  {showNewMessagesPill && (
                    <Button
                      onClick={scrollToBottom}
                      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-full"
                      size="sm"
                    >
                      New messages ↓
                    </Button>
                  )}
                </div>

                {/* Fixed Input Composer */}
                <div 
                  className="border-t bg-background p-3 md:p-4 sticky bottom-0 z-10 backdrop-blur-sm"
                  style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
                >
                  <div className="flex space-x-2">
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      disabled={isLoading}
                      className="flex-1"
                      aria-label="Type your message"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      size="sm"
                      className="px-3"
                      aria-label="Send message"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};